import { memo, useCallback } from 'react'

import { useQuery } from '@tanstack/react-query'
import { enqueueSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Header } from '@shared/components/Header'
import { Tabs } from '@shared/components/MaterialUIEnhancements/Tabs'
import { Content } from '@shared/layouts'
import { NavigationTabLink } from 'components/LinkedMUIComponents/NavigationTabLink'
import { ActionsMenu } from 'domains/ExpApps/ExpApp'
import { useAppUpdateMutation } from 'hooks/mutations/exp'
import { expAppRoute, expAppConfigRoute } from 'pages/NWPlatform/ExpApps/ExpApp'
import { appQueryKeys } from 'services/queryKeys'

const StructureRaw = () => {
  const { t } = useTranslation(['common', 'pages', 'entity'])

  const { expAppId: appId } = expAppRoute.useParams()

  const expAppQuery = useQuery(appQueryKeys.detail({ appId }))
  const expApp = expAppQuery.data

  const { mutate: updateApp, ...appUpdateMutation } = useAppUpdateMutation()

  const handleUpdateAvcTaskName = useCallback(
    (title: string, closeCallback: () => void) => {
      updateApp(
        {
          appId,
          expAppEdit: {
            ...expApp,
            name: title,
            config: {
              ...expApp?.config,
              custom: expApp?.config.custom ?? {},
              urlConfig: expApp?.config.urlConfig ?? { cdnUrl: '' },
              analyticsConfig: expApp?.config.analyticsConfig ?? { sampleRate: 0 },
              excludeSourceTypes: expApp?.config.excludeSourceTypes ?? [],
            },
          },
        },
        {
          onError: () => enqueueSnackbar(t('pages:ExpApp.updateError')),
          onSettled: closeCallback,
        },
      )
    },
    [t, updateApp, appId, expApp],
  )

  return (
    <Content
      HeaderSlot={
        <Header
          title={expAppQuery.data?.name ?? ''}
          isLoading={expAppQuery.isLoading}
          isSaving={appUpdateMutation.isPending}
          onSave={handleUpdateAvcTaskName}
          Actions={<ActionsMenu />}
        />
      }
      SubNavigation={
        <Tabs>
          <NavigationTabLink from={expAppRoute.to} to={expAppConfigRoute.to}>
            {t('pages:ExpApp.config')}
          </NavigationTabLink>
        </Tabs>
      }
    />
  )
}

export const Structure = memo(StructureRaw)
